// Modal — центровая модалка (замена правым drawer'ам).
// Фидбэк пилота (2026-06-03): карточки «улетали вправо», взгляд скакал слева
// направо. Теперь окно открывается по центру; размер задаётся разделом
// (sm | md | lg), плюс переключатель fullscreen для разделов, где нужен
// большой режим. Закрытие: backdrop, ✕, Esc. Блокирует скролл body, пока открыта.
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useEscapeToClose } from '@/lib/overlay-stack';
import { useIsMobile } from '@/lib/use-media';
import { cn } from '@/lib/utils';

import { OVERLAY_ROOT_CLASS, OverlayRoot } from './overlay-root';

export type ModalSize = 'sm' | 'md' | 'lg' | 'fullscreen';

/**
 * Стек открытых модалок: Esc закрывает ТОЛЬКО верхнюю.
 *
 * До этого каждое окно вешало свой обработчик на window, и при вложенности
 * (чат поверх карточки занятия, «Изменение в расписании» поверх неё же) один
 * Esc схлопывал всю стопку — пользователь вылетал из карточки занятия вместо
 * возврата в неё (правка владельца 2026-07-19).
 */
// Пресеты ширины. fullscreen занимает почти весь вьюпорт (для расписания и т.п.).
const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-md', // ~448px — компактные карточки/формы
  md: 'max-w-2xl', // ~672px — стандартная карточка
  lg: 'max-w-4xl', // ~896px — списки, таблицы, расписание
  fullscreen: 'max-w-[96vw]',
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Заголовок шапки. Строка → h2; либо произвольный узел (мульти-строчная шапка). */
  title?: ReactNode;
  /** Стартовый размер для раздела. По умолчанию md. */
  size?: ModalSize;
  /** Показывать кнопку «на весь экран». По умолчанию true. */
  allowFullscreen?: boolean;
  /** Доп. элементы в шапке слева от ⛶/✕ (бейджи, действия). */
  headerExtra?: ReactNode;
  /**
   * Стабильная высота окна: h-[85vh] вместо max-h. Контент разной длины
   * (переключение вкладок) не меняет размер окна — оно не «прыгает».
   */
  fixedHeight?: boolean;
  children: ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  allowFullscreen = true,
  headerExtra,
  fixedHeight = false,
  children,
}: ModalProps): JSX.Element | null {
  const [fullscreen, setFullscreen] = useState(false);

  // Сброс fullscreen при каждом новом открытии.
  useEffect(() => {
    if (open) setFullscreen(false);
  }, [open]);

  // Esc закрывает только ВЕРХНЕЕ перекрытие — общий стек с дроверами и
  // поповерами (lib/overlay-stack).
  const rootRef = useRef<HTMLDivElement>(null);
  useEscapeToClose(onClose, open, rootRef);

  // План 110: на телефоне окно любого размера занимает весь экран. Карточка в
  // 448px посреди 390-пиксельного экрана — это поля по 8px и содержимое в
  // колодце; полноэкранное окно даёт те же поля, что у самой страницы.
  // Хук ДО первого return (красная линия 4: хук после раннего выхода = React
  // #310 и белый экран).
  const isMobile = useIsMobile();

  // Блокировка скролла body, пока окно открыто.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  const effectiveSize: ModalSize = fullscreen || isMobile ? 'fullscreen' : size;
  // На телефоне 92vh оставляли бы бесполезную рамку сверху и снизу; dvh — а не
  // vh — потому что мобильные браузеры прячут адресную строку на скролле, и
  // 100vh там больше видимой области.
  const heightClass = isMobile
    ? 'h-[100dvh]'
    : fullscreen
      ? 'h-[92vh]'
      : fixedHeight
        ? 'h-[85vh]'
        : 'max-h-[92vh]';

  // В <body>, а не на месте: иначе окно наследует слой предка и уходит под
  // шапку и навигацию (см. overlay-root.tsx).
  return (
    <OverlayRoot>
      <div
        ref={rootRef}
        className={cn(
          OVERLAY_ROOT_CLASS,
          'fixed inset-0 z-50 flex items-center justify-center p-4 max-lg:p-0',
        )}
      >
        {/* Backdrop — клик закрывает; контент списка остаётся виден за блюром. */}
        <button
          type="button"
          aria-label="Закрыть"
          className="absolute inset-0 bg-ink-black/40 backdrop-blur-sm"
          onClick={onClose}
        />

        <div
          role="dialog"
          aria-modal="true"
          className={cn(
            'relative flex w-full flex-col rounded-card bg-canvas-white shadow-xl max-lg:rounded-none',
            // Полноэкранное окно начинается от края экрана — без отступов
            // безопасной зоны его шапка уезжает под часы, а нижние кнопки под
            // «домашнюю» полосу (скрин владельца 28.08).
            'max-lg:pt-[env(safe-area-inset-top)] max-lg:pb-[env(safe-area-inset-bottom)]',
            SIZE_CLASSES[effectiveSize],
            heightClass,
          )}
        >
          {/* Шапка */}
          <div className="flex items-start justify-between gap-3 border-b border-divider-gray px-6 py-5 max-lg:px-4 max-lg:py-3">
            <div className="min-w-0 flex-1">
              {typeof title === 'string' ? (
                <h2 className="truncate text-lg font-medium tracking-tight">{title}</h2>
              ) : (
                title
              )}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {headerExtra}
              {allowFullscreen && !isMobile ? (
                <button
                  type="button"
                  onClick={() => setFullscreen((v) => !v)}
                  className="text-muted-foreground hover:text-ink-black"
                  aria-label={fullscreen ? 'Свернуть' : 'На весь экран'}
                  title={fullscreen ? 'Свернуть' : 'На весь экран'}
                >
                  {fullscreen ? '⤡' : '⤢'}
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="text-muted-foreground hover:text-ink-black"
                aria-label="Закрыть"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Тело окна. overflow-y-auto: длинный контент скроллится ВНУТРИ окна, а
            не вываливается за край экрана (фидбэк: скроллился фон, а не данные).
            Модалки со своей внутренней прокруткой (flex-1 min-h-0 overflow-auto)
            заполняют тело целиком — двойного скролла не возникает. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
            {children}
          </div>
        </div>
      </div>
    </OverlayRoot>
  );
}
